import React, { useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TeacherFormDialog from "@/components/TeacherFormDialog";
import {
  ArrowLeft, Camera, Pencil, Mail, Phone, MapPin, IdCard, FileText, Upload, Trash2,
  Download, GraduationCap, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const KIND_LABEL = { cv: "CV", materiale: "Materiale didattico", altro: "Altro" };

export default function TeacherDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState("materiale");
  const [uploadLabel, setUploadLabel] = useState("");
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const [delDoc, setDelDoc] = useState(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const { data: teacher } = useQuery({
    queryKey: ["teacher", id],
    queryFn: async () => (await api.get(`/teachers/${id}`)).data,
  });

  const { data: documents = [] } = useQuery({
    queryKey: ["teacher-documents", id],
    queryFn: async () => (await api.get(`/teachers/${id}/documents`)).data,
  });

  const editMut = useMutation({
    mutationFn: async (p) => (await api.patch(`/teachers/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teacher", id] });
      toast.success("Docente aggiornato");
      setEditOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const photoMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/teachers/${id}/photo`, fd)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["teacher", id] }); toast.success("Foto aggiornata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore foto"),
  });

  const uploadMut = useMutation({
    mutationFn: async ({ file, label, kind }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || file.name);
      fd.append("kind", kind || "materiale");
      return (await api.post(`/teachers/${id}/documents`, fd)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teacher-documents", id] });
      toast.success("File caricato");
      setUploadLabel("");
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore upload"),
  });

  const delDocMut = useMutation({
    mutationFn: async (docId) => (await api.delete(`/teachers/documents/${docId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teacher-documents", id] });
      toast.success("File rimosso");
      setDelDoc(null);
    },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelDoc(null); },
  });

  if (!teacher) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const photoUrl = teacher.photo_path
    ? `${API_BASE}/teachers/${id}/photo?auth=${encodeURIComponent(token || "")}`
    : null;

  const cvDocs = documents.filter((d) => d.kind === "cv");
  const materialDocs = documents.filter((d) => d.kind === "materiale");
  const otherDocs = documents.filter((d) => d.kind === "altro");

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to="/scuola/docenti" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna ai docenti
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar profile */}
        <Card className="lg:col-span-1 p-6 border border-stone-200 rounded-xl shadow-none h-fit">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <div className="w-full h-full rounded-full bg-[#1C352D] text-white grid place-items-center text-3xl font-semibold overflow-hidden">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <>
                  {(teacher.first_name?.[0] || "?").toUpperCase()}
                  {(teacher.last_name?.[0] || "").toUpperCase()}
                </>
              )}
            </div>
            <button
              onClick={() => photoRef.current?.click()}
              data-testid="teacher-photo-btn"
              className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-[#E2725B] text-white grid place-items-center shadow hover:bg-[#c85d47] transition-colors duration-150"
            >
              <Camera size={16}/>
            </button>
            <input
              ref={photoRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && photoMut.mutate(e.target.files[0])}
            />
          </div>

          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{teacher.academic_title || "Docente"}</div>
            <h1 className="text-xl font-semibold text-[#1C352D] mt-0.5">
              {teacher.first_name} {teacher.last_name}
            </h1>
            {teacher.coordinated_area_name && (
              <Badge
                className="mt-2 text-white text-[10px] rounded-full"
                style={{ background: teacher.coordinated_area_color || "#1C352D" }}
              >
                Coordinatore · {teacher.coordinated_area_name}
              </Badge>
            )}
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            {teacher.email && <div className="flex items-center gap-2 text-stone-700"><Mail size={13} className="text-stone-400"/> {teacher.email}</div>}
            {teacher.phone && <div className="flex items-center gap-2 text-stone-700"><Phone size={13} className="text-stone-400"/> {teacher.phone}</div>}
            {teacher.codice_fiscale && <div className="flex items-center gap-2 text-stone-700 mono text-xs"><IdCard size={13} className="text-stone-400"/> {teacher.codice_fiscale}</div>}
            {(teacher.address || teacher.city) && <div className="flex items-center gap-2 text-stone-700"><MapPin size={13} className="text-stone-400"/> {[teacher.address, teacher.city, teacher.country].filter(Boolean).join(", ")}</div>}
          </div>

          <Button
            variant="outline"
            className="w-full mt-5 rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white transition-colors"
            onClick={() => setEditOpen(true)}
            data-testid="teacher-edit-btn"
          >
            <Pencil size={14} className="mr-1.5"/> Modifica anagrafica
          </Button>
        </Card>

        {/* Main tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="materiale">
            <TabsList className="bg-stone-100 rounded-full p-1">
              <TabsTrigger value="materiale" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-materiale">
                <BookOpen size={13} className="mr-1.5"/> Materiale didattico
              </TabsTrigger>
              <TabsTrigger value="cv" className="rounded-full data-[state=active]:bg-white" data-testid="tab-cv">
                <FileText size={13} className="mr-1.5"/> CV
              </TabsTrigger>
              <TabsTrigger value="info" className="rounded-full data-[state=active]:bg-white" data-testid="tab-info">
                <GraduationCap size={13} className="mr-1.5"/> Info
              </TabsTrigger>
            </TabsList>

            <TabsContent value="materiale">
              <Card className="p-6 border border-stone-200 rounded-xl shadow-none mt-4">
                <div className="mb-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <Label className="text-xs">Etichetta</Label>
                    <Input value={uploadLabel} onChange={(e) => setUploadLabel(e.target.value)} placeholder="es. Dispense Sistematica 1 - mod. 1" />
                  </div>
                  <Button
                    onClick={() => { setUploadKind("materiale"); fileRef.current?.click(); }}
                    data-testid="teacher-upload-material-btn"
                    className="rounded-full bg-[#E2725B] hover:bg-[#c85d47]"
                  >
                    <Upload size={14} className="mr-1.5"/> Carica file
                  </Button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadMut.mutate({ file: e.target.files[0], label: uploadLabel, kind: uploadKind })}
                />
                <DocList
                  docs={materialDocs}
                  emptyMsg="Nessun materiale caricato ancora."
                  onDelete={setDelDoc}
                  token={token}
                />
              </Card>
            </TabsContent>

            <TabsContent value="cv">
              <Card className="p-6 border border-stone-200 rounded-xl shadow-none mt-4">
                <Button
                  variant="outline"
                  onClick={() => { setUploadKind("cv"); setUploadLabel("CV"); fileRef.current?.click(); }}
                  data-testid="teacher-upload-cv-btn"
                  className="rounded-full mb-4"
                >
                  <Upload size={14} className="mr-1.5"/> Carica CV
                </Button>
                <DocList docs={cvDocs} emptyMsg="Nessun CV caricato." onDelete={setDelDoc} token={token} />
              </Card>
            </TabsContent>

            <TabsContent value="info">
              <Card className="p-6 border border-stone-200 rounded-xl shadow-none mt-4 space-y-4">
                <InfoRow label="Data di nascita" value={fmtDate(teacher.date_of_birth)} />
                <InfoRow label="Genere" value={teacher.gender || "—"} />
                <InfoRow label="Materie insegnate" value={teacher.subjects?.length ? teacher.subjects.join(", ") : "—"} />
                <InfoRow label="Bio" value={teacher.bio || "—"} multiline />
                <InfoRow label="Note" value={teacher.notes || "—"} multiline />
                {otherDocs.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Altri documenti</div>
                    <DocList docs={otherDocs} emptyMsg="—" onDelete={setDelDoc} token={token} compact />
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <TeacherFormDialog
        open={editOpen} onOpenChange={setEditOpen}
        mode="edit" initial={teacher}
        submitting={editMut.isPending}
        onSubmit={(p) => editMut.mutate(p)}
      />

      <AlertDialog open={!!delDoc} onOpenChange={(v) => { if (!v) setDelDoc(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere {delDoc?.label || "questo file"}?</AlertDialogTitle>
            <AlertDialogDescription>Il file verrà rimosso dalla scheda del docente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => delDoc && delDocMut.mutate(delDoc.id)}
            >
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoRow({ label, value, multiline }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{label}</div>
      <div className={`text-sm text-[#1C352D] ${multiline ? "whitespace-pre-line" : ""}`}>{value}</div>
    </div>
  );
}

function DocList({ docs, emptyMsg, onDelete, token, compact }) {
  if (!docs || docs.length === 0) {
    return <div className={`text-center text-stone-400 text-sm ${compact ? "py-2" : "py-10"}`}>{emptyMsg}</div>;
  }
  return (
    <ul className="divide-y divide-stone-100">
      {docs.map((d) => (
        <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`doc-${d.id}`}>
          <div className="w-9 h-9 rounded-lg bg-stone-100 grid place-items-center text-stone-500 shrink-0">
            <FileText size={16}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{d.label}</div>
            <div className="text-[11px] text-stone-500">
              {d.original_filename} · {(d.size / 1024).toFixed(0)} KB · {KIND_LABEL[d.kind] || d.kind}
            </div>
          </div>
          <a
            href={`${API_BASE}/teachers/documents/${d.id}/download?auth=${encodeURIComponent(token || "")}`}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]"
            title="Apri"
          >
            <Download size={15}/>
          </a>
          <button
            onClick={() => onDelete(d)}
            className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-700"
            data-testid={`doc-delete-${d.id}`}
          >
            <Trash2 size={15}/>
          </button>
        </li>
      ))}
    </ul>
  );
}
